/**
 * MobileAddEvaluationModal
 *
 * MUI full-screen Dialog replacement for AddEvaluationModal (PrimeReact Dialog).
 * Follows the same pattern as MobileAddContractModal, MobileAddWarning, etc.
 *
 * Components from MUI: Dialog, AppBar, Toolbar, IconButton, Typography, Box,
 *   Button, Slide, ToggleButtonGroup, ToggleButton, TextField, Skeleton
 */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// MUI Components
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Slide,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

// Redux
import { addNewlyEvaluated } from "../../../components/collabs/collabsSlice";
import {
  getEvaluationFields,
  setEvaluationAddModal,
  setOnEvaluateTeamId,
  setOnEvaluateCollab,
  setOnEvaluateDate,
  getMonthlyEvaluations,
  setMonthlyEvaluations,
  editEvaluation,
  setUpdateBody,
  setSelectedType,
  getEvaluationsTypes,
  getCollabsMissionsWithinTeam,
} from "../../../components/evaluations/evaluationsSlice";

// Shared utilities
import Langs from "../../../app/lang/langs";
import {
  blockageDaytoFutureMonth,
  checkPermission,
  isDayWithinAnyMission,
  isEmpty,
} from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileAddEvaluationModal(props) {
  const dispatch = useDispatch();

  // Redux selectors (exact same as desktop)
  const visible = useSelector(
    (state) => state.evaluations.evaluationAddModal
  );
  const collabs = useSelector((state) => state.collabs.list);
  const fields = useSelector((state) => state.evaluations.fields);
  const onEvaluateCollab = useSelector(
    (state) => state.evaluations.onEvaluateCollab
  );
  const onEvaluateTeamId = useSelector(
    (state) => state.evaluations.onEvaluateTeamId
  );
  const onEvaluateDate = useSelector(
    (state) => state.evaluations.onEvaluateDate
  );
  const monthlyEvaluations = useSelector(
    (state) => state.evaluations.monthlyEvaluations
  );
  const types = useSelector((state) => state.evaluations.types);
  const selectedType = useSelector(
    (state) => state.evaluations.selectedType
  );
  const selectedTypeGeneric = useSelector(
    (state) => state.evaluations.selectedTypeGeneric
  );
  const selectedTeamsIds = useSelector(
    (state) => state.teams.selectedTeamsIds
  );
  const missionsWithinTeam = useSelector(
    (state) => state.evaluations.collabsMissionsWithinTeam
  );

  const mounted = useRef(null);

  // Local state (exact same as desktop)
  const [currentFields, setCurrentFields] = useState([]);
  const [evaluationDate, setEvaluationDate] = useState();
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [errorFieldIds, setErrorFieldIds] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [allowedToEdit, setAllowedToEdit] = useState(false);

  /* ---------------------------------------------------------------- */
  /*  Effects (exact same logic as desktop AddEvaluationModal)         */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    }
  }, []);

  useEffect(() => {
    if (onEvaluateDate) {
      setEvaluationDate(onEvaluateDate);
    } else {
      setEvaluationDate(null);
    }
  }, [onEvaluateDate]);

  useEffect(() => {
    if (selectedType?.id === 4) {
      setAllowedToEdit(true);
      return;
    }

    let momentDate;
    if (!evaluationDate) {
      setAllowedToEdit(false);
      return;
    } else if (typeof evaluationDate === "number") {
      momentDate = moment(evaluationDate);
    } else if (evaluationDate instanceof Date) {
      momentDate = moment(evaluationDate);
    } else if (moment.isMoment(evaluationDate)) {
      momentDate = evaluationDate.clone();
    } else if (typeof evaluationDate === "string") {
      momentDate = moment(evaluationDate, "DD/MM/YYYY", true);
    } else {
      setAllowedToEdit(false);
      return;
    }
    if (momentDate.isValid()) {
      const formattedDate = momentDate
        .startOf("month")
        .format("DD/MM/YYYY");
      const editable = isDayWithinAnyMission(
        missionsWithinTeam,
        formattedDate
      );
      setAllowedToEdit(editable);
    } else {
      setAllowedToEdit(false);
    }
  }, [evaluationDate, missionsWithinTeam, selectedType]);

  useEffect(() => {
    const collab = onEvaluateCollab;
    if (collab) {
      setSelectedCollab(collab);
      reloadData(onEvaluateTeamId);
      dispatch(
        getCollabsMissionsWithinTeam({
          id_team: onEvaluateTeamId,
          id_collab: onEvaluateCollab?.id,
        })
      );
    } else {
      setSelectedCollab(null);
    }
  }, [collabs, onEvaluateCollab, onEvaluateTeamId, evaluationDate, selectedType]);

  useEffect(() => {
    if (fields) {
      let oldMonthlyEvaluationsMap = monthlyEvaluations.reduce(
        (obj, evaluation) => {
          obj[evaluation.id_field] = evaluation;
          return obj;
        },
        {}
      );

      setCurrentFields(
        fields.map((field) => {
          let fieldData = {
            id: field.id,
            rank: field.rank,
            title: field.title,
            comments: "",
          };
          if (
            oldMonthlyEvaluationsMap &&
            Object.keys(oldMonthlyEvaluationsMap).length
          ) {
            if (oldMonthlyEvaluationsMap[field.id]) {
              fieldData = {
                ...fieldData,
                value: oldMonthlyEvaluationsMap[field.id].value,
                comments:
                  oldMonthlyEvaluationsMap[field.id].comments || "",
                id_evaluation: oldMonthlyEvaluationsMap[field.id].id,
              };
            }
          }
          return fieldData;
        })
      );
    }
  }, [fields, monthlyEvaluations]);

  useEffect(() => {
    checkFields();
  }, [currentFields]);

  /* ---------------------------------------------------------------- */
  /*  Handlers (exact same logic as desktop)                           */
  /* ---------------------------------------------------------------- */

  const handleInputChange = (event) => {
    let newFieldsArray = [];
    currentFields.map((field) => {
      let newField = { ...field };
      if (field.id == event.target.id) {
        if (event.target.name == "value") {
          newField[event.target.name] = parseInt(event.target.value);
        } else {
          newField[event.target.name] = event.target.value;
        }
      }
      newFieldsArray.push(newField);
    });
    setCurrentFields(newFieldsArray);
  };

  const handleToggleChange = (fieldId, newValue) => {
    if (newValue === null) return; // don't allow deselect
    let newFieldsArray = [];
    currentFields.map((field) => {
      let newField = { ...field };
      if (field.id == fieldId) {
        newField.value = newValue;
      }
      newFieldsArray.push(newField);
    });
    setCurrentFields(newFieldsArray);
  };

  const handleCommentChange = (fieldId, commentValue) => {
    let newFieldsArray = [];
    currentFields.map((field) => {
      let newField = { ...field };
      if (field.id == fieldId) {
        newField.comments = commentValue;
      }
      newFieldsArray.push(newField);
    });
    setCurrentFields(newFieldsArray);
  };

  const handleDateChange = (event) => {
    setEvaluationDate(event);
  };

  function getYear(date = evaluationDate) {
    return moment(date).year();
  }

  function getMonth(date = evaluationDate) {
    return moment(date).month() + 1;
  }

  const handleClose = () => {
    dispatch(setEvaluationAddModal(false));
    dispatch(setOnEvaluateCollab(null));
    dispatch(setOnEvaluateTeamId(null));
    dispatch(setOnEvaluateDate(null));
    setCurrentFields([]);
    setSelectedCollab(null);
    setAllowedToEdit(false);
    dispatch(setMonthlyEvaluations([]));
    setIsEdit(false);
  };

  function checkFields() {
    let erroredIds = {};
    let isEditLocal = false;
    currentFields.map((field) => {
      if (
        field.value == 0 &&
        (!field.comments || !field.comments.length)
      ) {
        if (!erroredIds) erroredIds = {};
        erroredIds[field.id] = true;
      }
      if (!isEmpty(field.id_evaluation)) {
        isEditLocal = true;
      }
    });
    setErrorFieldIds(erroredIds);
    setIsEdit(isEditLocal);
    return Object.keys(erroredIds).length == 0;
  }

  const handleUpdate = () => {
    if (checkFields()) {
      let editBody = [];
      let newFields = currentFields.map((field) => {
        // default value = 1
        let updateValue = 1;
        // if set -> replace
        if (typeof field.value != "undefined") {
          updateValue = field.value;
        }
        let singleBody = {
          value: updateValue,
          comments: field.comments,
          year: getYear(),
          month: getMonth(),
          id_collab: onEvaluateCollab?.id,
          id_field: field.id,
        };
        if (typeof field.id_evaluation != "undefined")
          singleBody.id = field.id_evaluation;
        editBody.push(singleBody);

        field.value = updateValue;
        return field;
      });

      dispatch(
        addNewlyEvaluated([
          onEvaluateCollab?.id,
          editBody.filter((f) => !f.id).length,
        ])
      );

      dispatch(editEvaluation(editBody));

      if (
        getYear() == getYear(props.selectedDate) &&
        (!selectedTypeGeneric ||
          selectedType.id == selectedTypeGeneric.id)
      ) {
        // update evaluations view only when same year
        dispatch(
          setUpdateBody({
            month: getMonth(),
            id_collab: onEvaluateCollab?.id,
            id_team: selectedTeamsIds,
            matricule: selectedCollab.matricule,
            values: editBody.map((f) => f.value),
          })
        );
      }
      handleClose();
    }
  };

  function reloadData(idTeam) {
    if (!idTeam && selectedCollab) idTeam = selectedCollab.id_team;
    if (idTeam) {
      dispatch(getEvaluationsTypes({ idTeam: idTeam })).then(() => {
        let type = selectedType ? selectedType.id : null;
        dispatch(
          getEvaluationFields({ idType: type || 1, idTeam: idTeam })
        );
        if (evaluationDate) {
          dispatch(
            getMonthlyEvaluations({
              id_collab: onEvaluateCollab?.id,
              month: getMonth(),
              year: getYear(),
              idType: type || 1,
            })
          );
        }
      });
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Audit info renderers                                             */
  /* ---------------------------------------------------------------- */

  function formatIsoDateString(isoDateString) {
    const date = moment(isoDateString);
    let formattedTime = date.format("HH:mm");
    let formattedDate = date.format("DD/MM/YYYY");
    const now = moment();
    if (now.format("DD/MM/YYYY") == formattedDate) {
      return (
        <span>
          <Langs str="today at" />
          <b> {formattedTime}</b>
        </span>
      );
    }
    return (
      <span>
        <Langs str="the" />
        <b> {formattedDate} </b>
        <Langs str="at" />
        <b> {formattedTime}</b>
      </span>
    );
  }

  function renderCreatedByAndAt() {
    let data = monthlyEvaluations && monthlyEvaluations[0];
    if (data && data.creator && data.created_at) {
      return (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
          <Langs str="CREATED_BY" /> :{" "}
          <b>{data.creator}</b>{" "}
          {formatIsoDateString(data.created_at)}
        </Typography>
      );
    }
    return null;
  }

  function renderUpdatedByandAt() {
    let data = monthlyEvaluations && monthlyEvaluations[0];
    if (data && data.updater && data.updated_at) {
      return (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
          <Langs str="LAST_EDITED" /> :{" "}
          <b>{data.updater}</b>{" "}
          {formatIsoDateString(data.updated_at)}
        </Typography>
      );
    }
    return null;
  }

  /* ---------------------------------------------------------------- */
  /*  Derived state                                                    */
  /* ---------------------------------------------------------------- */

  const selectedTeamId = selectedCollab?.id_team;
  const isEditMode =
    selectedCollab &&
    blockageDaytoFutureMonth(evaluationDate) &&
    checkPermission(
      config.scopes.evaluations,
      config.permissionTypes.edit,
      selectedTeamId
    ) &&
    allowedToEdit;

  const canSubmit = isEditMode && fields?.length > 0;

  const formattedDate = evaluationDate
    ? moment(evaluationDate).format("MMMM YYYY")
    : "";

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <Dialog
      fullScreen
      open={!!visible}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
        },
      }}
    >
      {/* Top AppBar */}
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "scale(1.08)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 1.5, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "1.1rem",
                letterSpacing: "-0.02em",
                lineHeight: 1.3,
              }}
            >
              {isEdit ? (
                <Langs str="EDIT_EVALUATION" />
              ) : (
                <Langs str="ADD_EVALUATION" />
              )}
            </Typography>
            {selectedCollab && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  lineHeight: 1.3,
                }}
              >
                {selectedCollab.nom} {selectedCollab.prenom}
                {formattedDate ? ` — ${formattedDate}` : ""}
              </Typography>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Scrollable body with field cards */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          pb: 20,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        {/* Not-allowed-to-edit message */}
        {selectedCollab && !isEditMode && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "12px",
              bgcolor: "rgba(211,47,47,0.06)",
              border: "1px solid rgba(211,47,47,0.15)",
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#d32f2f", fontWeight: 500 }}
            >
              Pas disponible
            </Typography>
          </Box>
        )}

        {/* Field cards */}
        {currentFields.length === 0 && visible && (
          <>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={140}
                sx={{ borderRadius: "16px", mb: 2 }}
              />
            ))}
          </>
        )}

        {currentFields.map((field, index) => {
          const hasError = !!errorFieldIds[field.id];
          return (
            <Box
              key={field.id}
              sx={{
                background: "rgba(255,255,255,0.65)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                borderRadius: "16px",
                border: hasError
                  ? "1px solid rgba(211,47,47,0.3)"
                  : "1px solid rgba(255,255,255,0.45)",
                boxShadow:
                  "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
                p: 2,
                mb: 2,
                transition: TRANSITION_SMOOTH,
                animation: `fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) ${
                  index * 0.05
                }s both`,
              }}
            >
              {/* Field title */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                  color: hasError ? "#d32f2f" : "text.primary",
                  fontSize: "0.92rem",
                }}
              >
                <Langs str={field.title} />
                {hasError && (
                  <Typography
                    component="span"
                    sx={{ color: "#d32f2f", ml: 0.5 }}
                  >
                    *
                  </Typography>
                )}
              </Typography>

              {/* YES / NO Toggle */}
              <ToggleButtonGroup
                value={field.value}
                exclusive
                onChange={(_, newVal) => {
                  if (newVal !== null) {
                    handleToggleChange(field.id, newVal);
                  }
                }}
                disabled={!isEditMode}
                fullWidth
                sx={{ mb: 1.5 }}
              >
                <ToggleButton
                  value={1}
                  sx={{
                    flex: 1,
                    borderRadius: "12px 0 0 12px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    py: 1.2,
                    gap: 0.5,
                    transition: TRANSITION_SMOOTH,
                    "&.Mui-selected": {
                      bgcolor: "rgba(46,125,50,0.12)",
                      color: "#2e7d32",
                      borderColor: "rgba(46,125,50,0.3)",
                      "&:hover": {
                        bgcolor: "rgba(46,125,50,0.18)",
                      },
                    },
                  }}
                >
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "1.1rem" }}
                  />
                  <Langs str="YES" />
                </ToggleButton>
                <ToggleButton
                  value={0}
                  sx={{
                    flex: 1,
                    borderRadius: "0 12px 12px 0",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    py: 1.2,
                    gap: 0.5,
                    transition: TRANSITION_SMOOTH,
                    "&.Mui-selected": {
                      bgcolor: "rgba(211,47,47,0.1)",
                      color: "#d32f2f",
                      borderColor: "rgba(211,47,47,0.3)",
                      "&:hover": {
                        bgcolor: "rgba(211,47,47,0.16)",
                      },
                    },
                  }}
                >
                  <CancelOutlinedIcon sx={{ fontSize: "1.1rem" }} />
                  <Langs str="NO" />
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Comments */}
              <TextField
                multiline
                minRows={2}
                maxRows={5}
                fullWidth
                placeholder="Commentaire..."
                value={field.comments || ""}
                disabled={!isEditMode}
                onChange={(e) =>
                  handleCommentChange(field.id, e.target.value)
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: BORDER_RADIUS_INPUT,
                    backgroundColor: "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(6px)",
                    transition: TRANSITION_SMOOTH,
                    "& fieldset": {
                      borderColor: hasError
                        ? "rgba(211,47,47,0.4)"
                        : "rgba(0,0,0,0.08)",
                      transition: TRANSITION_SMOOTH,
                    },
                    "&:hover fieldset": {
                      borderColor: hasError
                        ? "rgba(211,47,47,0.5)"
                        : "rgba(0,0,0,0.18)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: hasError
                        ? "rgba(211,47,47,0.6)"
                        : "rgba(255,144,21,0.5)",
                      boxShadow: hasError
                        ? "0 0 0 3px rgba(211,47,47,0.12)"
                        : "0 0 0 3px rgba(255,144,21,0.12)",
                    },
                  },
                  "& textarea": {
                    fontSize: "16px",
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>

      {/* Sticky bottom action area */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          bgcolor: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
          borderTop: "1px solid rgba(255,255,255,0.5)",
          zIndex: 1,
        }}
      >
        {/* Created by / Updated by info */}
        {renderCreatedByAndAt()}
        {renderUpdatedByandAt()}

        {/* Action button */}
        {canSubmit ? (
          <Button
            variant="contained"
            fullWidth
            onClick={handleUpdate}
            sx={{
              mt: 1,
              minHeight: 52,
              borderRadius: BORDER_RADIUS_BUTTON,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1.05rem",
              letterSpacing: "-0.01em",
              bgcolor: "#FF9015",
              boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                bgcolor: "#e8820f",
                transform: "scale(1.02)",
                boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            {isEdit ? (
              <Langs str="edit" />
            ) : (
              <Langs str="ADD_EVALUATION" />
            )}
          </Button>
        ) : selectedCollab ? (
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "#d32f2f",
              fontWeight: 500,
              mt: 1,
              py: 1,
            }}
          >
            Pas disponible
          </Typography>
        ) : null}
      </Box>
    </Dialog>
  );
}
